import {
  createMediator,
  type Mediator,
  type MediatorTransitionResult,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export interface DockingState {
  step: "age" | "boards" | "install";
  isAdult: boolean;
  hasVisitedBoards: boolean;
  isInstalling: boolean;
  installError: boolean;
  completed: boolean;
}
export type DockingEvent =
  | { type: "docking.age-changed"; isAdult: boolean }
  | { type: "docking.non-adult-board-available" }
  | { type: "docking.next"; fromStep: DockingState["step"] }
  | {
      type:
        | "docking.back"
        | "docking.install"
        | "docking.skip"
        | "docking.installed";
    }
  | { type: "docking.install-result"; succeeded: boolean };
export interface DockingEnvironment {
  includesAdultBoard: boolean;
  requiresAdultConfirmation: boolean;
  skipInstall: boolean;
}
export type DockingInput = DockingEvent & { environment: DockingEnvironment };
export type DockingEffect =
  | { type: "complete"; isAdult: boolean }
  | { type: "install" };

export function isDockingEvent(event: TypedEvent): event is DockingEvent {
  return [
    "docking.age-changed",
    "docking.non-adult-board-available",
    "docking.next",
    "docking.back",
    "docking.install",
    "docking.skip",
    "docking.installed",
    "docking.install-result",
  ].includes(event.type);
}

function complete(
  state: DockingState,
): MediatorTransitionResult<DockingState, DockingEffect> {
  return {
    status: "handled",
    state: { ...state, completed: true, isInstalling: false },
    effects: [{ type: "complete", isAdult: state.isAdult }],
  };
}

export function transitionDocking(
  state: DockingState,
  event: DockingInput,
): MediatorTransitionResult<DockingState, DockingEffect> {
  if (state.completed) return { status: "handled", state };
  switch (event.type) {
    case "docking.age-changed":
      return { status: "handled", state: { ...state, isAdult: event.isAdult } };
    case "docking.non-adult-board-available":
      return state.step === "age" && !state.hasVisitedBoards
        ? { status: "handled", state: { ...state, step: "boards" } }
        : { status: "handled", state };
    case "docking.back":
      return {
        status: "handled",
        state:
          state.step === "age" && state.hasVisitedBoards
            ? { ...state, step: "boards" }
            : state,
      };
    case "docking.next": {
      if (event.fromStep !== state.step) return { status: "handled", state };
      if (state.step === "install" || (state.step === "age" && !state.isAdult))
        return { status: "handled", state };
      if (state.step === "age" && !state.hasVisitedBoards)
        return { status: "handled", state: { ...state, step: "boards" } };
      const next =
        state.step === "boards" ? { ...state, hasVisitedBoards: true } : state;
      if (
        state.step === "boards" &&
        (event.environment.includesAdultBoard ||
          event.environment.requiresAdultConfirmation) &&
        !state.isAdult
      )
        return { status: "handled", state: { ...next, step: "age" } };
      return event.environment.skipInstall
        ? complete(next)
        : { status: "handled", state: { ...next, step: "install" } };
    }
    case "docking.install":
      return state.step !== "install" || state.isInstalling
        ? { status: "handled", state }
        : {
            status: "handled",
            state: { ...state, isInstalling: true, installError: false },
            effects: [{ type: "install" }],
          };
    case "docking.install-result":
      return state.step !== "install"
        ? { status: "handled", state }
        : event.succeeded
          ? complete(state)
          : {
              status: "handled",
              state: { ...state, isInstalling: false, installError: true },
            };
    case "docking.skip":
    case "docking.installed":
      return state.step === "install"
        ? complete(state)
        : { status: "unhandled" };
  }
}

export function createDockingMediator(
  hasNonAdultBoard: boolean,
  runEffect: (effect: DockingEffect) => void,
): Mediator<DockingState, DockingInput, DockingEffect> {
  return createMediator<DockingState, DockingInput, DockingEffect>(
    {
      step: hasNonAdultBoard ? "boards" : "age",
      isAdult: false,
      hasVisitedBoards: false,
      isInstalling: false,
      installError: false,
      completed: false,
    },
    transitionDocking,
    { runEffect },
  );
}
